import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Eye, EyeOff, Loader2, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  // Check for referral code from localStorage
  useEffect(() => {
    const storedRef = localStorage.getItem('referral_code');
    if (storedRef) {
      setReferralCode(storedRef);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      const user = await signup(email, password, name);
      
      // Track referral signup if there's a referral code
      if (referralCode && user?.id) {
        await axios.post(`${API}/referral/track-signup?ref_code=${referralCode}&new_user_id=${user.id}`).catch(() => {});
        localStorage.removeItem('referral_code'); // Clear after tracking
      }
      
      toast.success("Account created! Let's get to know you.");
      navigate("/onboarding");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Coach Coop" className="h-12 w-12 rounded-sm" />
          <span className="font-black text-2xl tracking-tighter">COACH COOP</span>
        </Link>
        
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-2xl font-black uppercase tracking-tighter text-center">
              START YOUR TRANSFORMATION
            </CardTitle>
            {referralCode && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-center gap-2 text-green-500 text-sm bg-green-500/10 py-2 px-4 rounded-lg mt-2"
              >
                <Gift className="h-4 w-4" />
                <span>Referred by a friend!</span>
              </motion.div>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name" className="uppercase text-sm tracking-wider">Name</Label>
                <Input
                  id="name"
                  type="text"
                  data-testid="signup-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-input border-transparent focus:border-primary rounded-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="uppercase text-sm tracking-wider">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="signup-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="bg-input border-transparent focus:border-primary rounded-sm"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="uppercase text-sm tracking-wider">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="signup-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    className="bg-input border-transparent focus:border-primary rounded-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="uppercase text-sm tracking-wider">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  data-testid="signup-confirm-password-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="bg-input border-transparent focus:border-primary rounded-sm"
                />
              </div>
              
              <Button
                type="submit"
                data-testid="signup-submit-btn"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
              </Button>
            </form>
            
            <div className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{" "}
              <Link to="/login" className="text-primary hover:underline font-semibold">
                Sign In
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
